import { useState, useCallback, useEffect } from 'react';
import { useGetHomepageConfig, useUpdateHomepageConfig } from '../api/layout/useHomepageConfigApi';

const OLD_HERO_SUBTITLE =
  'Sun-soaked beaches, vibrant cities, and hidden gems — all at the best guaranteed prices.';
const NEW_HERO_SUBTITLE =
  'From relaxing beaches to vibrant city breaks, explore handpicked trips made for real travelers.';
const DEFAULT_MARQUEE_ITEMS = [
  'Best Price Guarantee',
  '10,000+ Holidays',
  'No Booking Fees',
  'Secure Payments',
  '24/7 Support',
  'Trusted by 2M+ Travelers',
  'Free Cancellation',
  'Award-Winning Service',
];

const defaultConfig = {
  hero: {
    badgeText: 'Discover 10,000+ holidays worldwide',
    title: 'Where will you chase the sun?',
    subtitle: NEW_HERO_SUBTITLE,
    searchButtonText: 'Search',
  },
  searchFields: [
    { key: 'destination', label: 'Destination', value: 'Where to?' },
    { key: 'departure', label: 'Departure', value: 'Pick a date' },
    { key: 'duration', label: 'Duration', value: '7 nights' },
    { key: 'travelers', label: 'Travelers', value: '2 adults' },
  ],
  categories: [],
  featuredHolidayTypes: [],
  destinationTabs: [],
  stats: [],
  vacationTypes: [],
  popularHotels: [],
  popularDestinationGroups: [],
  trustItems: [],
  marqueeItems: DEFAULT_MARQUEE_ITEMS,
  reviews: [],
  awards: [],
  newsletter: {
    title: 'Never miss a sunny deal',
    subtitle: 'Subscribe for exclusive offers, travel inspiration, and early access deals.',
    inputPlaceholder: 'Your email address',
    buttonText: 'Subscribe',
  },
  sectionHeaders: {},
  sectionVisibility: {},
  logo: { mainUrl: '', lightUrl: '' },
};

const toStringSafe = (value) => (value == null ? '' : String(value));

function normalizeMarqueeItems(value) {
  if (!Array.isArray(value)) return [...DEFAULT_MARQUEE_ITEMS];
  return value
    .map((item) => (typeof item === 'string' ? item : item?.text ?? ''))
    .filter((item) => item !== null && item !== undefined);
}

export function sanitizeHomepagePayload(payload = {}) {
  const asArray = (value) => (Array.isArray(value) ? value : []);
  const cleanStringArray = (value) =>
    asArray(value)
      .map((item) => toStringSafe(item).trim())
      .filter(Boolean);

  const sanitizeDestinationTabs = asArray(payload.destinationTabs).map((tab = {}) => ({
    ...tab,
    tab: toStringSafe(tab.tab).trim(),
    cards: asArray(tab.cards).map((card = {}) => ({
      ...card,
      name: toStringSafe(card.name).trim(),
      holidays: toStringSafe(card.holidays).trim(),
      imageUrl: toStringSafe(card.imageUrl).trim(),
      badge: toStringSafe(card.badge).trim(),
    })),
  }));

  // A group link as an object: its text, what it filters by and the emoji it shows. Empty
  // parts are left out, so the website falls back to its own choice for each.
  const sanitizeGroupLink = (item) => {
    const d = item.dest;
    const icon = toStringSafe(item.icon).trim();
    return {
      label: toStringSafe(item.label).trim(),
      ...(d && d.code && (d.type === 'country' || d.type === 'city')
        ? {
            dest: {
              type: d.type,
              code: toStringSafe(d.code).trim().toUpperCase(),
              name: toStringSafe(d.name).trim(),
              countryName: toStringSafe(d.countryName).trim(),
            },
          }
        : {}),
      ...(item.boardCode ? { boardCode: toStringSafe(item.boardCode).trim().toUpperCase() } : {}),
      ...(item.holidayTypeId != null && item.holidayTypeId !== ''
        ? { holidayTypeId: Number(item.holidayTypeId) }
        : {}),
      ...(icon ? { icon } : {}),
    };
  };

  const sanitizeGroups = asArray(payload.popularDestinationGroups).map((group = {}) => {
    const links =
      Array.isArray(group.links)
        ? group.links
        : toStringSafe(group.links)
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
    // Icon, colour and "View all" are optional; left out, the website picks them itself.
    const { icon, tone, viewAll, ...rest } = group;
    const cleanIcon = toStringSafe(icon).trim();
    const cleanTone = toStringSafe(tone).trim();
    const cleanViewAll = viewAll && typeof viewAll === 'object' ? sanitizeGroupLink(viewAll) : null;
    const viewAllIsSet =
      cleanViewAll && (cleanViewAll.label || Object.keys(cleanViewAll).some((k) => k !== 'label'));
    return {
      ...rest,
      title: toStringSafe(group.title).trim(),
      count: toStringSafe(group.count).trim(),
      ...(cleanIcon ? { icon: cleanIcon } : {}),
      ...(cleanTone ? { tone: cleanTone } : {}),
      ...(viewAllIsSet ? { viewAll: cleanViewAll } : {}),
      // A link is either a legacy plain string (label only) or an object that
      // also carries what to filter by. Without the object branch below,
      // toStringSafe would turn every linked entry into "[object Object]".
      links: links
        .map((item) => (item && typeof item === 'object' ? sanitizeGroupLink(item) : toStringSafe(item).trim()))
        .filter((l) => (typeof l === 'string' ? l : l.label)),
    };
  });

  return {
    ...payload,
    hero: {
      ...(payload.hero || {}),
      badgeText: toStringSafe(payload.hero?.badgeText).trim(),
      title: toStringSafe(payload.hero?.title).trim(),
      subtitle: toStringSafe(payload.hero?.subtitle).trim(),
      searchButtonText: toStringSafe(payload.hero?.searchButtonText).trim(),
      backgroundImageUrl: toStringSafe(payload.hero?.backgroundImageUrl).trim(),
    },
    searchFields: asArray(payload.searchFields).map((field = {}) => ({
      ...field,
      key: toStringSafe(field.key).trim(),
      label: toStringSafe(field.label).trim(),
      value: toStringSafe(field.value).trim(),
    })),
    categories: asArray(payload.categories).map((item = {}) => ({
      ...item,
      title: toStringSafe(item.title).trim(),
      count: toStringSafe(item.count).trim(),
      imageUrl: toStringSafe(item.imageUrl).trim(),
    })),
    featuredHolidayTypes: asArray(payload.featuredHolidayTypes).map((item = {}) => ({
      ...item,
      holidayTypeId: item.holidayTypeId != null ? Number(item.holidayTypeId) : null,
      title: toStringSafe(item.title).trim(),
      imageUrl: toStringSafe(item.imageUrl).trim(),
      active: item.active !== false,
      // Destinations revealed when the homepage card is expanded. Only well-formed
      // entries are sent; the backend caps the list and strips anything unknown.
      destinations: asArray(item.destinations)
        .filter((d) => d && d.code && (d.type === 'country' || d.type === 'city'))
        .map((d) => ({
          type: d.type === 'country' ? 'country' : 'city',
          code: toStringSafe(d.code).trim().toUpperCase(),
          name: toStringSafe(d.name).trim(),
          countryName: toStringSafe(d.countryName).trim(),
        })),
    })),
    destinationTabs: sanitizeDestinationTabs,
    stats: asArray(payload.stats).map((item = {}) => ({
      ...item,
      value: toStringSafe(item.value).trim(),
      label: toStringSafe(item.label).trim(),
    })),
    vacationTypes: asArray(payload.vacationTypes).map((item = {}) => ({
      // `...item` is what carries `search` (the card's filter criteria) and `boardCode`
      // through to the API — they are deliberately not restated below because they are not
      // strings to trim. Replacing the spread with an explicit field list would silently
      // drop the whole link config and leave every card unclickable.
      ...item,
      label: toStringSafe(item.label).trim(),
      title: toStringSafe(item.title).trim(),
      description: toStringSafe(item.description).trim(),
      imageUrl: toStringSafe(item.imageUrl).trim(),
      buttonText: toStringSafe(item.buttonText).trim(),
    })),
    popularHotels: asArray(payload.popularHotels).map((item = {}) => ({
      ...item,
      name: toStringSafe(item.name).trim(),
      location: toStringSafe(item.location).trim(),
      price: toStringSafe(item.price).trim(),
      score: toStringSafe(item.score).trim(),
      imageUrl: toStringSafe(item.imageUrl).trim(),
    })),
    popularDestinationGroups: sanitizeGroups,
    trustItems: asArray(payload.trustItems).map((item = {}) => ({
      ...item,
      title: toStringSafe(item.title).trim(),
      description: toStringSafe(item.description).trim(),
    })),
    marqueeItems: cleanStringArray(payload.marqueeItems),
    reviews: asArray(payload.reviews).map((item = {}) => ({
      ...item,
      name: toStringSafe(item.name).trim(),
      date: toStringSafe(item.date).trim(),
      text: toStringSafe(item.text).trim(),
      score: toStringSafe(item.score).trim(),
      avatarUrl: toStringSafe(item.avatarUrl).trim(),
    })),
    awards: asArray(payload.awards).map((item = {}) => ({
      ...item,
      title: toStringSafe(item.title).trim(),
      year: toStringSafe(item.year).trim(),
    })),
    newsletter: {
      ...(payload.newsletter || {}),
      title: toStringSafe(payload.newsletter?.title).trim(),
      subtitle: toStringSafe(payload.newsletter?.subtitle).trim(),
      inputPlaceholder: toStringSafe(payload.newsletter?.inputPlaceholder).trim(),
      buttonText: toStringSafe(payload.newsletter?.buttonText).trim(),
    },
    logo: {
      mainUrl: toStringSafe(payload.logo?.mainUrl).trim(),
      lightUrl: toStringSafe(payload.logo?.lightUrl).trim(),
    },
  };
}

function mapApiToLocal(apiConfig) {
  if (!apiConfig) return { ...defaultConfig };
  const hero = apiConfig.hero ?? defaultConfig.hero;
  const normalizedHeroSubtitle =
    hero?.subtitle === OLD_HERO_SUBTITLE ? NEW_HERO_SUBTITLE : hero?.subtitle;

  return {
    hero: {
      ...hero,
      subtitle: normalizedHeroSubtitle ?? defaultConfig.hero.subtitle,
    },
    searchFields: Array.isArray(apiConfig.searchFields) ? apiConfig.searchFields : [],
    categories: Array.isArray(apiConfig.categories) ? apiConfig.categories : [],
    // Without these two, the load path silently drops the saved featured cards
    // and section-header overrides, so the editor always reopened empty.
    featuredHolidayTypes: Array.isArray(apiConfig.featuredHolidayTypes)
      ? apiConfig.featuredHolidayTypes
      : [],
    destinationTabs: Array.isArray(apiConfig.destinationTabs) ? apiConfig.destinationTabs : [],
    stats: Array.isArray(apiConfig.stats) ? apiConfig.stats : [],
    vacationTypes: Array.isArray(apiConfig.vacationTypes) ? apiConfig.vacationTypes : [],
    popularHotels: Array.isArray(apiConfig.popularHotels) ? apiConfig.popularHotels : [],
    popularDestinationGroups: Array.isArray(apiConfig.popularDestinationGroups)
      ? apiConfig.popularDestinationGroups
      : [],
    trustItems: Array.isArray(apiConfig.trustItems) ? apiConfig.trustItems : [],
    marqueeItems: normalizeMarqueeItems(apiConfig.marqueeItems),
    reviews: Array.isArray(apiConfig.reviews) ? apiConfig.reviews : [],
    awards: Array.isArray(apiConfig.awards) ? apiConfig.awards : [],
    newsletter: apiConfig.newsletter ?? defaultConfig.newsletter,
    sectionHeaders:
      apiConfig.sectionHeaders && typeof apiConfig.sectionHeaders === 'object'
        ? apiConfig.sectionHeaders
        : {},
    sectionVisibility:
      apiConfig.sectionVisibility && typeof apiConfig.sectionVisibility === 'object'
        ? apiConfig.sectionVisibility
        : {},
    logo:
      apiConfig.logo && typeof apiConfig.logo === 'object'
        ? apiConfig.logo
        : { mainUrl: '', lightUrl: '' },
  };
}

export function useHomepageConfig() {
  const [config, setConfig] = useState(() => ({ ...defaultConfig }));
  const [lastSaved, setLastSaved] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  const getApi = useGetHomepageConfig();
  const updateApi = useUpdateHomepageConfig();

  const load = useCallback(async () => {
    try {
      const res = await getApi.execute();
      const homepageConfig = res?.data?.homepageConfig ?? null;
      const mapped = mapApiToLocal(homepageConfig);
      setConfig(mapped);
      setLastSaved(mapped);
      setIsDirty(false);
      return homepageConfig;
    } catch (err) {
      if (err.response?.status === 404) {
        setConfig({ ...defaultConfig });
        setLastSaved(null);
        return null;
      }
      throw err;
    }
  }, [getApi.execute]);

  useEffect(() => {
    load();
  }, [load]);

  const updateField = useCallback((key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
  }, []);

  const save = useCallback(async () => {
    const sanitizedPayload = sanitizeHomepagePayload(config);
    const res = await updateApi.execute(sanitizedPayload);
    const updated = res?.data?.homepageConfig ?? null;
    const mapped = mapApiToLocal(updated);
    setConfig(mapped);
    setLastSaved(mapped);
    setIsDirty(false);
    return updated;
  }, [config, updateApi.execute]);

  const reset = useCallback(() => {
    if (lastSaved) {
      setConfig({ ...lastSaved });
      setIsDirty(false);
    } else {
      load();
    }
  }, [lastSaved, load]);

  return {
    config,
    loading: getApi.loading,
    saving: updateApi.loading,
    error: getApi.error || updateApi.error,
    isDirty,
    updateField,
    load,
    save,
    reset,
  };
}
